// Per-user speed settings (Settings → General), each a multiplier where
// 1 = the built-in default:
//  - zoom: SceneHandler.js's zoomBy() step (scroll wheel, pinch).
//  - move: keyboard movement (W/A/S/D/Q/E) and the move joystick
//    (three/touchJoystick.js).
//  - look: looking around — arrow-key turning / tilting and the look joystick.
//
// Stored per USER in DynamoDB (backend getUserPreferences /
// setUserPreferences, keyed on the caller's Cognito sub) so they follow them
// across devices and models. localStorage mirrors them: read synchronously
// at scene init so the first wheel notch / key press already uses them,
// refreshed from the backend right after. Anonymous viewers (logged-out
// share links) never touch the backend at all — their settings live only in
// this browser's localStorage (and the backend would 401 them anyway: it
// keys on the caller's verified Cognito sub, which they don't have).
import { fetchAuthSession } from 'aws-amplify/auth';
import { authFetch } from './apiHelper';
import { API_URL } from '../config';

export const SPEED_MIN = 0.25;
export const SPEED_MAX = 4;
export const SPEED_DEFAULT = 1;

// storageKey: localStorage; field: backend response attribute; param:
// setUserPreferences request field.
export const ZOOM_SPEED = { storageKey: 'zoomSpeed', field: 'ZoomSpeed', param: 'zoomSpeed' };
export const MOVE_SPEED = { storageKey: 'moveSpeed', field: 'MoveSpeed', param: 'moveSpeed' };
export const LOOK_SPEED = { storageKey: 'lookSpeed', field: 'LookSpeed', param: 'lookSpeed' };

const clamp = (v) => Math.min(SPEED_MAX, Math.max(SPEED_MIN, v));

export function readCachedSpeed(pref) {
  try {
    const v = parseFloat(localStorage.getItem(pref.storageKey));
    return Number.isFinite(v) ? clamp(v) : SPEED_DEFAULT;
  } catch (e) {
    return SPEED_DEFAULT;
  }
}

async function isLoggedIn() {
  try {
    const session = await fetchAuthSession();
    return !!session.tokens?.idToken;
  } catch (e) {
    return false;
  }
}

function cacheSpeed(pref, v) {
  try {
    if (v === SPEED_DEFAULT) localStorage.removeItem(pref.storageKey);
    else localStorage.setItem(pref.storageKey, String(v));
  } catch (e) { /* private mode */ }
}

// One getUserPreferences request shared by every caller for a moment, so the
// scene and the settings modal reading both speeds cost one round trip.
let pending = null;
function fetchPreferences() {
  if (!pending) {
    pending = (async () => {
      if (!(await isLoggedIn())) return null;
      try {
        const res = await authFetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          mode: 'cors',
          body: JSON.stringify({ getUserPreferences: true }),
        });
        return res.ok ? await res.json() : null;
      } catch (e) {
        return null;
      }
    })();
    pending.finally(() => setTimeout(() => { pending = null; }, 2000));
  }
  return pending;
}

// Resolves to the user's saved speed, or null when it can't be read (logged
// out, network error) — callers keep whatever they already had.
export async function fetchSpeed(pref) {
  const data = await fetchPreferences();
  // A backend without this field falls through to some other 200 response
  // — only trust a reply that actually carries it.
  if (!data || !(pref.field in data)) return null;
  const v = typeof data[pref.field] === 'number' ? clamp(data[pref.field]) : SPEED_DEFAULT;
  cacheSpeed(pref, v);
  return v;
}

// Always updates the local copy; logged-in users also get a best-effort
// backend write.
export async function saveSpeed(pref, v) {
  const value = clamp(v);
  cacheSpeed(pref, value);
  if (!(await isLoggedIn())) return value;
  try {
    await authFetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      mode: 'cors',
      body: JSON.stringify({ setUserPreferences: { [pref.param]: value === SPEED_DEFAULT ? null : value } }),
    });
  } catch (e) { /* best-effort */ }
  return value;
}
